
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDatabase, ref, onValue, off, get, set } from 'firebase/database';
import MainLayout from './MainLayout';
import ReportSheet from './ReportSheet';
import './ScoreSheet.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// Import your logo
import logo from '../pages/logo.png';


// Stable constants outside the component
const ALL_TERMS = ['FIRST TERM', 'SECOND TERM', 'THIRD TERM'];
const PSYCHOMOTOR_SKILLS = ['Handwriting', 'Verbal Fluency', 'Games and sports', 'Gymnastics', 'Music', 'Construction'];
const AFFECTIVE_AREAS = ['Attentiveness', 'Punctuality', 'Neatness', 'Politeness', 'Relationship with others', 'Honesty'];

const GRADING_SCALE = [
    { range: '75-100', grade: 'A', remark: 'Excellent' },
    { range: '65-74', grade: 'B', remark: 'Very Good' },
    { range: '55-64', grade: 'C', remark: 'Good' },
    { range: '45-54', grade: 'D', remark: 'Fair' },
    { range: '40-44', grade: 'E', remark: 'Pass' },
    { range: '0-39', grade: 'F', remark: 'Fail' },
];

const getGradeAndRemark = (average) => {
    const avg = Math.round(average);
    if (avg >= 75) return { grade: 'A', remark: 'Excellent' };
    if (avg >= 65) return { grade: 'B', remark: 'Very Good' };
    if (avg >= 55) return { grade: 'C', remark: 'Good' };
    if (avg >= 45) return { grade: 'D', remark: 'Fair' };
    if (avg >= 40) return { grade: 'E', remark: 'Pass' };
    return { grade: 'F', remark: 'Fail' };
};

const ScoreSheet = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState({ paid: false, checking: true });
  const [allClasses, setAllClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('FIRST TERM');
  const [selectedSession, setSelectedSession] = useState(
    `${new Date().getFullYear()}/${new Date().getFullYear() + 1}`
  );
  
  const [students, setStudents] = useState([]);
  const [processedData, setProcessedData] = useState(null);
  const [termSummaries, setTermSummaries] = useState({});
  const [cumulativeData, setCumulativeData] = useState(null);
  const [gradingStructure, setGradingStructure] = useState({});
  const [traitsData, setTraitsData] = useState({});
  const [formMasterName, setFormMasterName] = useState('');
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [viewingReportFor, setViewingReportFor] = useState(null);
  const [subjectDetails, setSubjectDetails] = useState({});

  const [view, setView] = useState('summary');
  const [studentScores, setStudentScores] = useState({});
  const [subjects, setSubjects] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const db = getDatabase();
  const sessions = Array.from({ length: 5 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return `${year}/${year + 1}`;
  });

  useEffect(() => {
    if (!currentUser) return;
    const userRef = ref(db, `users/${currentUser.uid}`);
    const callback = onValue(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setUserData(data);
        const role = data.role?.toLowerCase();
        if (role === 'form-master') {
          setSelectedClass(data.assignedClass || '');
        } else if (role === 'student') {
          setSelectedClass(data.class || data.studentClass || '');
        }
      }
    });
    return () => off(userRef, 'value', callback);
  }, [currentUser, db]);

  useEffect(() => {
    if (!currentUser || userData?.role?.toLowerCase() !== 'student') {
        setPaymentStatus({ paid: true, checking: false });
        return;
    }
    const invoicesRef = ref(db, `invoices/${currentUser.uid}`);
    const callback = onValue(invoicesRef, (snapshot) => {
        const hasPending = snapshot.exists() && Object.values(snapshot.val()).some(inv => inv.status.toLowerCase() !== 'paid');
        setPaymentStatus({ paid: !hasPending, checking: false });
    });
    return () => off(invoicesRef, 'value', callback);
  }, [currentUser, db, userData]);

  useEffect(() => {
    const role = userData?.role?.toLowerCase();
    if (['admin', 'principal'].includes(role)) {
      const classesRef = ref(db, 'classes');
      const callback = onValue(classesRef, (snapshot) => {
        setAllClasses(snapshot.exists() ? Object.keys(snapshot.val()).sort() : []);
      });
      return () => off(classesRef, 'value', callback);
    }
  }, [userData, db]);

  useEffect(() => {
    if (!selectedClass) return;
    const name = selectedClass.toLowerCase();
    let structure = {};
    if (name.includes('playgroup') || name.includes('kg') || name.includes('nursery')) {
      structure = { Assignment: 20, Test: 20, Exam: 60 };
    } else if (name.includes('primary') || name.includes('jss')) {
      structure = { '1st Assign': 10, '2nd Assign': 10, '1st Test': 10, '2nd Test': 10, Exam: 60 };
    } else if (name.includes('sss')) {
      structure = { '1st Assign': 5, '2nd Assign': 5, '1st Test': 10, '2nd Test': 10, Exam: 70 };
    }
    setGradingStructure(structure);
  }, [selectedClass]);

  const handleSearch = useCallback(async () => {
    if (!selectedClass) return alert('Please select a class.');
    setLoading(true);
    setError(null);

    try {
        const sessionPath = selectedSession.replace('/', '-');
        
        const [schoolSnap, usersSnap, traitsSnap, scoresSnap] = await Promise.all([
            get(ref(db, 'school')),
            get(ref(db, 'users')),
            get(ref(db, `traits/${sessionPath}/${selectedTerm}/${selectedClass}`)),
            get(ref(db, `ca_scores/${sessionPath}/${selectedTerm}/${selectedClass}`))
        ]);

        setSchoolInfo(schoolSnap.val());
        const allUsers = usersSnap.val() || {};
        setTraitsData(traitsSnap.val() || {});
        const scoresData = scoresSnap.val() || {};
        const classSubjects = Object.keys(scoresData);
        setSubjects(classSubjects);

        const formMaster = Object.values(allUsers).find(u => u.role?.toLowerCase() === 'form-master' && u.assignedClass === selectedClass);
        setFormMasterName(formMaster ? formMaster.fullName || formMaster.name : 'N/A');

        let filteredStudents = Object.values(allUsers)
            .filter(user => user.role?.toLowerCase() === 'student' && (user.class === selectedClass || user.studentClass === selectedClass))
            .map(student => ({ ...student, id: student.uid, studentId: student.studentId || 'N/A' }));

        if (userData?.role?.toLowerCase() === 'student') {
            filteredStudents = filteredStudents.filter(s => s.id === currentUser.uid);
        }
        setStudents(filteredStudents);

        if (filteredStudents.length === 0) {
            setLoading(false);
            return;
        }

        const studentTermAverages = {};
        const termScoresDataForSelectedTerm = scoresData;

        for (const term of ALL_TERMS) {
            const termScoresData = (term === selectedTerm) ? termScoresDataForSelectedTerm : (await get(ref(db, `ca_scores/${sessionPath}/${term}/${selectedClass}`))).val() || {};
            if (Object.keys(termScoresData).length > 0) {
                filteredStudents.forEach(student => {
                    let totalScore = 0;
                    const subjectsInTerm = Object.keys(termScoresData);
                    subjectsInTerm.forEach(subject => {
                        const studentSubjectScores = termScoresData[subject]?.[student.id] || {};
                        totalScore += Object.values(studentSubjectScores).reduce((acc, score) => acc + (Number(score) || 0), 0);
                    });
                    const termAverage = subjectsInTerm.length > 0 ? (totalScore / subjectsInTerm.length) : 0;
                    if (!studentTermAverages[student.id]) studentTermAverages[student.id] = {};
                    studentTermAverages[student.id][term] = termAverage;
                });
            }
        }
        setTermSummaries(studentTermAverages);

        const cumulativeAvgs = {};
        filteredStudents.forEach(student => {
            const termAvgs = studentTermAverages[student.id] ? Object.values(studentTermAverages[student.id]) : [];
            const cumulativeAvg = termAvgs.length > 0 ? termAvgs.reduce((a, b) => a + b, 0) / termAvgs.length : 0;
            const { grade, remark } = getGradeAndRemark(cumulativeAvg);
            cumulativeAvgs[student.id] = { average: cumulativeAvg, grade, remark };
        });
        setCumulativeData(cumulativeAvgs);
        
        const subjectDetailsData = {};
        classSubjects.forEach(subject => {
            const studentScoresForSubject = filteredStudents.map(student => {
                const scores = termScoresDataForSelectedTerm[subject]?.[student.id] || {};
                return Object.values(scores).reduce((acc, score) => acc + (Number(score) || 0), 0);
            });
            if (studentScoresForSubject.length > 0) {
                subjectDetailsData[subject] = {
                    classAverage: studentScoresForSubject.reduce((a, b) => a + b, 0) / studentScoresForSubject.length,
                    classHighest: Math.max(...studentScoresForSubject),
                    classLowest: Math.min(...studentScoresForSubject),
                };
            }
        });
        setSubjectDetails(subjectDetailsData);

        const studentResults = filteredStudents.map(student => {
            let totalScoreForTerm = 0;
            const scoresBySubject = {};
            classSubjects.forEach(subject => {
                const studentScores = termScoresDataForSelectedTerm[subject]?.[student.id] || {};
                scoresBySubject[subject] = studentScores;
                totalScoreForTerm += Object.values(studentScores).reduce((acc, score) => acc + (Number(score) || 0), 0);
            });
            const averageForTerm = classSubjects.length > 0 ? (totalScoreForTerm / classSubjects.length) : 0;
            return { ...student, scores: scoresBySubject, totalScore: totalScoreForTerm, average: averageForTerm };
        });

        studentResults.sort((a, b) => b.totalScore - a.totalScore);
        studentResults.forEach((student, index) => student.position = index + 1);
        
        setProcessedData({ results: studentResults, subjects: classSubjects });
        setStudentScores(studentResults.reduce((acc, student) => ({...acc, [student.id]: student.scores }), {}));

    } catch (err) {
        console.error(err);
        setError('An error occurred during data fetching.');
    } finally {
        setLoading(false);
    }
}, [db, selectedClass, selectedSession, selectedTerm, currentUser, userData]);

  const handleScoreChange = (studentId, subject, ca, value) => {
      const maxScore = gradingStructure[ca] || 100;
      const numValue = value === '' ? '' : Number(value);
      if (value === '' || (!isNaN(numValue) && numValue >= 0 && numValue <= maxScore)) {
          setStudentScores(prev => ({
              ...prev,
              [studentId]: { ...prev[studentId], [subject]: { ...prev[studentId]?.[subject], [ca]: numValue } }
          }));
      }
  };

  const handleSaveScores = async (studentId, subject) => {
      const sessionPath = selectedSession.replace('/', '-');
      const scoresToSave = studentScores[studentId]?.[subject];
      if (!scoresToSave) return;
      const scoreRef = ref(db, `ca_scores/${sessionPath}/${selectedTerm}/${selectedClass}/${subject}/${studentId}`);
      try {
          await set(scoreRef, scoresToSave);
          alert('Scores updated successfully!');
          handleSearch(); // Re-fetch data to show the latest updates
      } catch (error) {
          alert('Failed to update scores.');
      }
  };
  
  const handleViewReport = (student) => setViewingReportFor(student);

  const role = userData?.role?.toLowerCase();
  const canSelectFilters = ['admin', 'principal', 'form-master'].includes(role);
  
  if (viewingReportFor) {
      return (
          <MainLayout>
              <button className="btn btn-secondary mb-3" onClick={() => setViewingReportFor(null)}>Back to Summary</button>
              <ReportSheet 
                  student={viewingReportFor}
                  scores={viewingReportFor.scores}
                  term={selectedTerm}
                  session={selectedSession}
                  schoolInfo={schoolInfo}
                  traits={traitsData[viewingReportFor.id]}
                  formMasterName={formMasterName}
                  gradingStructure={gradingStructure}
                  subjectDetails={subjectDetails}
                  students={students}
              />
          </MainLayout>
      );
  }

  if (paymentStatus.checking) return <MainLayout><div className="loading-container"><div className="spinner"></div><p>Verifying payment status...</p></div></MainLayout>;
  if (!paymentStatus.paid) return <MainLayout><div className="access-denied-container"><div className="access-denied-card"><i className="fas fa-lock lock-icon"></i><h2>Access to Scoresheet Denied</h2><p>Please ensure all your school fees are paid to gain access.</p><a href="/payments" className="btn-pay-fees">Go to Payments</a></div></div></MainLayout>;

  return (
    <MainLayout>
      <div className="scoresheet-container container-fluid">
        {schoolInfo && <div className="school-header text-center mb-4"><img src={logo} alt="School Logo" style={{maxWidth: '100px', marginBottom: '10px'}}/><h3>{schoolInfo.schoolName}</h3><p>{schoolInfo.address}</p><p>Email: {schoolInfo.email} | Phone: {schoolInfo.phone}</p></div>}
        <h4>Consolidated Score Sheet</h4>
        <div className="filters-container row mb-3">
          <div className="col-md-3"><label>Session</label><select className="form-control" value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)} disabled={!canSelectFilters}>{sessions.map(s => <option key={s}>{s}</option>)}</select></div>
          <div className="col-md-3"><label>Term</label><select className="form-control" value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)} disabled={!canSelectFilters}>{ALL_TERMS.map(t => <option key={t}>{t}</option>)}</select></div>
          <div className="col-md-3"><label>Class</label><select className="form-control" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} disabled={!canSelectFilters || role === 'form-master' || role === 'student'}>{role === 'admin' || role === 'principal' ? <><option value="">-- Select Class --</option>{allClasses.map(c => <option key={c} value={c}>{c}</option>)}</> : <option value={selectedClass}>{selectedClass}</option>}</select></div>
          <div className="col-md-3 align-self-end"><button className="btn btn-primary w-100" onClick={handleSearch} disabled={loading}>{loading ? 'Searching...' : 'Search'}</button></div>
        </div>
        <div className="view-toggle-buttons my-3">
            <button className={`btn ${view === 'summary' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('summary')}>Summary View</button>
            <button className={`btn ${view === 'update' ? 'btn-primary' : 'btn-secondary'} ml-2`} onClick={() => setView('update')}>Update Scores</button>
            <Link to={`/master-report-sheet?session=${selectedSession}&term=${selectedTerm}&class=${selectedClass}`} className="btn btn-info ml-2">Master Report Sheet</Link>
        </div>
        {loading && <p>Loading...</p>}
        {error && <div className="alert alert-danger">{error}</div>}
        
        {!loading && !error && view === 'update' && <div className="update-scores-view"><h3>Update Continuous Assessment (CA)</h3>{students.map(student => <StudentScoreCard key={student.id} student={student} subjects={subjects} gradingStructure={gradingStructure} scores={studentScores[student.id] || {}} onScoreChange={handleScoreChange} onSave={handleSaveScores} selectedSession={selectedSession} selectedTerm={selectedTerm} selectedClass={selectedClass} />)}</div>}
        
        {!loading && !error && view === 'summary' && processedData && processedData.results.length > 0 && (
          <>
            <p><strong>Form Master:</strong> {formMasterName}</p>
            <h5 className="mt-4">{selectedTerm} - DETAILED RESULTS</h5>
            <div className="table-responsive mt-3">
              <table className="table table-bordered table-striped">
                 <thead>
                  <tr>
                    <th rowSpan="2">Pos</th><th rowSpan="2">Student Name</th>
                    {processedData.subjects.map((subject) => (<th key={subject} colSpan={Object.keys(gradingStructure).length + 2}>{subject}</th>))}
                    <th rowSpan="2">Total</th><th rowSpan="2">Average</th>
                    <th rowSpan="2">Actions</th>
                  </tr>
                  <tr>
                    {processedData.subjects.map((subject) => (
                      <React.Fragment key={`${subject}-sub`}>
                        {Object.keys(gradingStructure).map(ca => <th key={ca}>{ca}</th>)}
                        <th>Total</th><th>Grade</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {processedData.results.map((student) => (
                    <tr key={student.id}>
                      <td>{student.position}</td><td>{student.fullName || student.name}</td>
                      {processedData.subjects.map((subject) => {
                        const subjectScores = student.scores[subject] || {};
                        const subjectTotal = Object.values(subjectScores).reduce((a, s) => a + (Number(s) || 0), 0);
                        const { grade } = getGradeAndRemark(subjectTotal);
                        return (
                          <React.Fragment key={`${student.id}-${subject}`}>
                            {Object.keys(gradingStructure).map(ca => (<td key={ca}>{subjectScores[ca] || 0}</td>))}
                            <td>{subjectTotal}</td><td>{grade}</td>
                          </React.Fragment>
                        )
                      })}
                      <td>{student.totalScore.toFixed(2)}</td><td>{student.average.toFixed(2)}</td>
                      <td><button className="btn btn-sm btn-info" onClick={() => handleViewReport(student)}>View Report</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='row mt-5'>
                <div className='col-lg-8'>
                    <h5 className="mb-3">SESSION SUMMARY</h5>
                    <table className="table table-bordered">
                        <thead>
                            <tr><th>Student</th>{ALL_TERMS.map(term => <th key={term}>{term} Avg</th>)}<th>Cumulative Avg</th><th>Grade</th><th>Remark</th></tr>
                        </thead>
                        <tbody>
                            {students.map(student => (
                                <tr key={student.id}>
                                    <td>{student.fullName || student.name}</td>
                                    {ALL_TERMS.map(term => (<td key={term}>{(termSummaries[student.id]?.[term] || 0).toFixed(2)}</td>))}
                                    <td>{cumulativeData?.[student.id]?.average.toFixed(2)}</td>
                                    <td>{cumulativeData?.[student.id]?.grade}</td>
                                    <td>{cumulativeData?.[student.id]?.remark}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className='col-lg-4'>
                    <h5 className="mb-3">GRADING SCALE</h5>
                    <table className="table table-sm table-bordered">
                        <thead className='thead-light'><tr><th>Score Range</th><th>Grade</th><th>Remark</th></tr></thead>
                        <tbody>{GRADING_SCALE.map(g => (<tr key={g.grade}><td>{g.range}</td><td>{g.grade}</td><td>{g.remark}</td></tr>))}</tbody>
                    </table>
                </div>
            </div>

            <h5 className="mt-5">TRAITS ASSESSMENT ({selectedTerm})</h5>
            <div className="table-responsive mt-3">
                <table className="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>Student</th>
                            {PSYCHOMOTOR_SKILLS.map(skill => <th key={skill}>{skill}</th>)}
                            {AFFECTIVE_AREAS.map(area => <th key={area}>{area}</th>)}
                            <th>Teacher's Remark</th><th>Principal's Remark</th>
                        </tr>
                    </thead>
                    <tbody>
                        {students.map(student => (
                            <tr key={student.id}>
                                <td>{student.fullName || student.name}</td>
                                {PSYCHOMOTOR_SKILLS.map(skill => (<td key={skill}>{traitsData[student.id]?.psychomotor?.[skill] || '-'}</td>))}
                                {AFFECTIVE_AREAS.map(area => (<td key={area}>{traitsData[student.id]?.affective?.[area] || '-'}</td>))}
                                <td>{traitsData[student.id]?.teacherRemark || '-'}</td><td>{traitsData[student.id]?.principalRemark || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

const StudentScoreCard = ({ student, subjects, gradingStructure, scores, onScoreChange, onSave, selectedSession, selectedTerm, selectedClass }) => {
    return (
        <div className="card mb-4">
            <div className="card-header font-weight-bold">{student.fullName || student.name}: {student.studentId}</div>
            <div className="card-body">
                <div className="row mb-3">
                    <div className="col-md-2 text-center"><img src={student.photoURL || 'https://via.placeholder.com.com/100'} alt="Student" className="rounded-circle" width="100" height="100" /></div>
                    <div className="col-md-10"><p><strong>Session:</strong> {selectedSession}</p><p><strong>Term:</strong> {selectedTerm}</p><p><strong>Class:</strong> {selectedClass}</p></div>
                </div>
                {Array.isArray(subjects) && subjects.map(subject => (
                    <div key={subject} className="mb-4">
                        <h5 className="font-weight-bold">{subject}</h5>
                        <div className="row">
                            {Object.entries(gradingStructure).map(([ca, max]) => (
                                <div className="col-md-2" key={ca}><div className="form-group"><label>{ca}</label><input type="number" className="form-control" value={scores[subject]?.[ca] || ''} onChange={(e) => onScoreChange(student.id, subject, ca, e.target.value)} min="0" max={max} /><small className="form-text text-muted">Max: {max}</small></div></div>
                            ))}
                             <div className="col-md-2 d-flex align-items-end"><button className="btn btn-success btn-block" onClick={() => onSave(student.id, subject)}><i className="fas fa-save"></i> Update</button></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ScoreSheet;
